import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, formatDistanceToNow, differenceInHours, isSameYear } from 'date-fns';
import { api, supabase } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import Spinner from '../../components/ui/Spinner';
import Tooltip from '../../components/ui/Tooltip';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiMessageSquare, FiFileText, FiCalendar } = FiIcons;

const CALL_OUTCOME_LABELS = {
  answered: 'Answered',
  voicemail: 'Voicemail',
  machine: 'Voicemail',
  no_answer: 'No Answer',
  busy: 'Busy',
  callback_requested: 'Callback Requested',
  booked_meeting: 'Booked Meeting',
  closed_business: 'Closed Business',
};

const APPOINTMENT_TYPE_LABELS = {
  intro_call: 'Intro Call',
  presentation: 'Presentation',
  follow_up: 'Follow-Up',
  closing_call: 'Closing Call',
};

function formatCallOutcome(outcome) {
  if (!outcome) return '—';
  return CALL_OUTCOME_LABELS[outcome] ?? String(outcome).replace(/_/g, ' ');
}

function formatCallDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatRelativeTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';

  if (differenceInHours(now, d) < 48) {
    return formatDistanceToNow(d, { addSuffix: true });
  }

  return format(d, isSameYear(d, now) ? 'MMM d' : 'MMM d, yyyy');
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, isSameYear(d, new Date()) ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy h:mm a');
}

function formatAppointmentDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return format(d, isSameYear(d, new Date()) ? 'MMM d' : 'MMM d, yyyy');
}

function formatAppointmentType(type) {
  if (!type) return '—';
  return APPOINTMENT_TYPE_LABELS[type] ?? String(type).replace(/_/g, ' ');
}

function agentNameFromItem(item) {
  return item.user?.full_name || item.agent_name || '—';
}

function getActivityIcon(type) {
  switch (type) {
    case 'call': return FiPhone;
    case 'sms': return FiMessageSquare;
    case 'note': return FiFileText;
    case 'appointment': return FiCalendar;
    default: return FiFileText;
  }
}

function getActivityDescription(item) {
  switch (item.type) {
    case 'call':
      return `Call · ${formatCallOutcome(item.outcome)} · ${formatCallDuration(item.duration_seconds)}`;
    case 'sms':
      return item.direction === 'inbound' ? 'SMS received' : 'SMS sent';
    case 'note':
      return 'Note added';
    case 'appointment':
      return `Appointment booked · ${formatAppointmentDate(item.scheduled_at)}`;
    default:
      return 'Activity';
  }
}

function TooltipLines({ lines }) {
  return (
    <>
      {lines.map((line) => (
        <div key={line.label}>
          <span className="text-gray-300">{line.label}: </span>
          {line.value}
        </div>
      ))}
    </>
  );
}

function buildActivityTooltip(item) {
  const ts = item.activity_at || item.called_at || item.created_at || item.scheduled_at;

  switch (item.type) {
    case 'call': {
      const lines = [
        { label: 'Outcome', value: formatCallOutcome(item.outcome) },
        { label: 'Duration', value: formatCallDuration(item.duration_seconds) },
        { label: 'Agent', value: agentNameFromItem(item) },
        { label: 'Date', value: formatDateTime(ts) },
      ];
      if (item.notes?.trim()) {
        lines.push({ label: 'Notes', value: item.notes.trim() });
      }
      return <TooltipLines lines={lines} />;
    }
    case 'sms':
      if (item.direction === 'inbound') {
        return (
          <TooltipLines
            lines={[
              { label: 'Message', value: item.body || '—' },
              { label: 'From', value: item.from_number || '—' },
              { label: 'Date', value: formatDateTime(ts) },
            ]}
          />
        );
      }
      return (
        <TooltipLines
          lines={[
            { label: 'Message', value: item.message || item.body || '—' },
            { label: 'Sent by', value: agentNameFromItem(item) },
            { label: 'Date', value: formatDateTime(ts) },
          ]}
        />
      );
    case 'note':
      return (
        <TooltipLines
          lines={[
            { label: 'Note', value: item.notes?.trim() || '—' },
            { label: 'Added by', value: agentNameFromItem(item) },
            { label: 'Date', value: formatDateTime(ts) },
          ]}
        />
      );
    case 'appointment': {
      const lines = [
        { label: 'Title', value: item.title || '—' },
        { label: 'Type', value: formatAppointmentType(item.appointment_type) },
        { label: 'Assigned to', value: item.assignee?.full_name || '—' },
        { label: 'Date', value: formatDateTime(item.scheduled_at || ts) },
        { label: 'Status', value: item.status || '—' },
      ];
      if (item.notes?.trim()) {
        lines.push({ label: 'Notes', value: item.notes.trim() });
      }
      return <TooltipLines lines={lines} />;
    }
    default:
      return null;
  }
}

export default function RecentActivityPanel({ leadId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const loadActivity = useCallback(async () => {
    if (!leadId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const activity = await api.leads.getRecentActivity(leadId, { limit: 8 });
      setItems(activity || []);
    } catch (err) {
      console.error('[workspace] activity load failed', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    setItems([]);
    loadActivity();
  }, [loadActivity]);

  useEffect(() => {
    if (!leadId || !supabase) return undefined;

    let debounceId = null;
    const scheduleLoad = () => {
      if (debounceId != null) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => {
        debounceId = null;
        loadActivity();
      }, 1500);
    };

    const channel = supabase
      .channel(`workspace-activity-${leadId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'call_logs', filter: `lead_id=eq.${leadId}` },
        scheduleLoad,
      )
      .subscribe();

    return () => {
      if (debounceId != null) window.clearTimeout(debounceId);
      supabase.removeChannel(channel);
    };
  }, [leadId, loadActivity]);

  return (
    <div className="shrink-0 border-t border-gray-200 flex flex-col" style={{ height: 280 }}>
      <div className="px-4 py-3 border-b border-gray-100 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900">Recent Activity</h3>
      </div>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-2">
        {!leadId ? (
          <p className="text-xs text-gray-400 py-4 text-center">No activity yet</p>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="h-5 w-5 text-primary" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-xs text-gray-400 py-4 text-center">No activity yet</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => {
              const ts = item.activity_at || item.called_at || item.created_at;
              const tooltip = buildActivityTooltip(item);
              const row = (
                <li className="flex items-start gap-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors">
                  <div className="mt-0.5 w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <SafeIcon icon={getActivityIcon(item.type)} className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 leading-snug">
                      {getActivityDescription(item)}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {formatRelativeTimestamp(ts)}
                    </p>
                  </div>
                </li>
              );

              if (!tooltip) {
                return <React.Fragment key={`${item.type}-${item.id}`}>{row}</React.Fragment>;
              }

              return (
                <Tooltip
                  key={`${item.type}-${item.id}`}
                  content={tooltip}
                  boundaryRef={scrollRef}
                >
                  {row}
                </Tooltip>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
